import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Table,
  Space,
  PageHeader,
  Menu,
  Dropdown,
  Button,
  Tooltip,
  Modal,
  notification,
} from "antd";
import {
  MoreOutlined,
  CloudDownloadOutlined,
  InfoCircleOutlined,
  EditOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { labListingSaga } from "./saga";
import * as selectors from "./selectors";
import routes from "../../../../../routes";
import { reverse } from "named-urls";
import AddOperator from "../../../../forms/AddOperatorForm";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup.js";
import { labOperatorSchema } from "../../../../schemas/labOperatorSchema";
import { useAuth } from "../../../../contexts/auth";

const { Column } = Table;

const MENU_ACTIONS = {
  viewLabPartnerDetails: "VIEW_LAB_PARTNER_DETAILS",
  editLabPartnerDetails: "EDIT_LAB_PARTNER_DETAILS",
  downloadContract: "DOWNLOAD_CONTRACT",
  downloadTestingScope: "DOWNLOAD_SCOPE",
  addLabOperator: "ADD_LAB_OPERATOR",
};

const LabListing = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: labListingSaga });
  const dispatch = useDispatch();
  const history = useHistory();
const auth = useAuth();
  const user = auth.loggedInUser;
  const userRole = user.role;

  useEffect(() => {
    dispatch(actions.fetchLabs());
  }, [dispatch]);

  const labs = useSelector(selectors.selectlabs) || [];
  const loading = useSelector(selectors.selectLoading);
  // const downloadsInProgress = useSelector(selectors.selectDownloadStatus);
  const [isLabOperatorModalVisible, setIsLabOperatorModalVisible] =
    useState(false);
  const [selectedLab, setSelectedLab] = useState(null);
  const [isLabOperatorAddingOrEditing, setIsLabOperatorAddingOrEditing] =
    useState(false);

  const isLabOperatorEdit = selectedLab?.labAppUser?._id ? true : false;
  const operatorValidationSchema = useMemo(() => {
    return labOperatorSchema({ requirePassword: !isLabOperatorEdit });
  }, [isLabOperatorEdit]);

  const {
    control: addOrEditLabOperatorControl,
    handleSubmit: addOrEditLabOperatorHandleSubmit,
    formState: { errors: addOrEditLabOperatorErrors },
    reset: addOrEditLabOperatorReset,
    watch: addOrEditLabOperatorWatch,
  } = useForm({
    resolver: yupResolver(operatorValidationSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      lab: "",
    },
  });

  const labData = labs.map((lab) => ({
    id: lab._id,
    name: lab.name,
    region: lab?.operatingStates?.map((state) => state.name).join(", "),
    contactPhone: lab?.pocContact ? lab?.pocContact : lab?.poc.phone,
    contactName: lab?.pocName
      ? lab?.pocName
      : `${lab.poc.firstName} ${lab.poc.lastName}`,
    contract: lab.contract,
    testingScope: lab.testingScope,
    labAppOperator: lab.labAppUser
      ? `${lab.labAppUser.firstName} ${lab.labAppUser.lastName}`
      : "-",
    labData: lab,
  }));

  const onClick = ({ key, id, data }) => {
    switch (key) {
      case MENU_ACTIONS.viewLabPartnerDetails:
        history.push(
          reverse(`${routes.backOffice.labPartners.details.view}`, {
            labId: id,
          })
        );
        break;
      case MENU_ACTIONS.editLabPartnerDetails:
        history.push(
          reverse(`${routes.backOffice.labPartners.details.edit}`, {
            labId: id,
          })
        );
        break;
      case MENU_ACTIONS.downloadContract:
        dispatch(
          actions.downloadDocument({
            id,
            data: { fileName: data.contract },
          })
        );
        break;
      case MENU_ACTIONS.downloadTestingScope:
        dispatch(
          actions.downloadDocument({
            id,
            data: { fileName: data.testingScope },
          })
        );
        break;

      case MENU_ACTIONS.addLabOperator:
        setIsLabOperatorModalVisible(true);
        addOrEditLabOperatorReset({
          firstName: data?.labData?.labAppUser?.firstName || "",
          lastName: data?.labData?.labAppUser?.lastName || "",
          email: data?.labData?.labAppUser?.email || "",
          phone: data?.labData?.labAppUser?.phone || "",
          // password: "",
          lab: id,
        });
        setSelectedLab(data?.labData);
        break;
      default:
        break;
    }
  };

  const getMenuItems = (lab) => {
    return (
      <Menu onClick={({ key }) => onClick({ id: lab.id, key, data: lab })}>
        <Menu.Item
          key={MENU_ACTIONS.viewLabPartnerDetails}
          icon={<InfoCircleOutlined />}
        >
          {"View Lab Partner Details"}
        </Menu.Item>
        { userRole !== "csc_admin" && (
          <>
        <Menu.Item
          key={MENU_ACTIONS.editLabPartnerDetails}
          icon={<EditOutlined />}
        >
          {"Edit Lab Partner Details"}
        </Menu.Item>
        {/*Need to improve Lab Module*/}
        {lab.contract && lab.contract.length > 0 && (
          <Menu.Item
            disabled={!(lab?.contract !== "")}
            key={MENU_ACTIONS.downloadContract}
            icon={<CloudDownloadOutlined />}
          >
            {lab?.contract !== "" ? "Download Contract" : "Upload Contract"}
          </Menu.Item>
        )}

        {lab.testingScope && lab.contract.length > 0 && (
          <Menu.Item
            disabled={!(lab?.testingScope !== "")}
            key={MENU_ACTIONS.downloadTestingScope}
            icon={<CloudDownloadOutlined />}
          >
            {lab?.testingScope !== ""
              ? "Download Testing Scope"
              : "Download Testing Scope"}
          </Menu.Item>
        )}

        <Menu.Item key={MENU_ACTIONS.addLabOperator} icon={<UserAddOutlined />}>
          {lab?.labData?.labAppUser?._id
            ? "Edit Lab Operator"
            : "Add Lab Operator"}
        </Menu.Item>
        </>
        )}
      </Menu>
    );
  };


  const handleAddOrEditLabOperator = (data) => {
    setIsLabOperatorAddingOrEditing(true);
    dispatch(
      actions.addOrEditLabOperator({
        data: data,
        onSuccess: () => {
          notification.success({
            message: "Lab Operator updated successfully",
          });
          handleCancelAddOrEditLabOperator();
          dispatch(actions.fetchLabs());
        },
        onFailure: (error) => {
          setIsLabOperatorAddingOrEditing(false);
          notification.error({
            message: "Failed to update Lab Operator",
            description: error.message || "An error occurred",
          });
        },
      })
    );
  };

  const handleCancelAddOrEditLabOperator = () => {
    setIsLabOperatorModalVisible(false);
    setIsLabOperatorAddingOrEditing(false);
    addOrEditLabOperatorReset();
    setSelectedLab(null);
  };

  return (
    <div style={{ height: "100%" }}>
      <PageHeader
        ghost={false}
        title="Lab Partners"
        extra={[
          userRole !== "support_team" && userRole !== "csc_admin" && (
          <Button key="1" type="primary">
            <Link to={routes.backOffice.labPartners.new}>Add New Lab</Link>
          </Button>
          )
        ]}
      />
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        <Table
  dataSource={labData}
  loading={loading}
  rowKey="id"
  pagination={{
    defaultPageSize: 10,
    pageSizeOptions: ["10", "30", "50", "100"],
    showSizeChanger: true,
  }}
>
          <Column title="Lab Partner" dataIndex="name" key="name" />
          <Column title="Region of Operation" dataIndex="region" key="region" />
          <Column
            title="Contact Number"
            dataIndex="contactPhone"
            key="contactPhone"
          />
          <Column
            title="Point of Contact"
            dataIndex="contactName"
            key="contactName"
          />
          <Column
            title="Lab App Operator"
            dataIndex="labAppOperator"
            key="labAppOperator"
          />

       {userRole !== "support_team" && (   
  <Column
    title="Action"
    key="action"
    render={(text, record) => (
      <Space size="middle">
        <Dropdown overlay={getMenuItems(record)}>
          <Tooltip title="Actions">
            <Button
              type="text"
              shape="circle"
              icon={<MoreOutlined />}
            />
          </Tooltip>
        </Dropdown>
      </Space>
    )}
  />
       )}

        </Table>
      </div>
      {/* Add or Edit Lab operator  */}
      <Modal
        title={isLabOperatorEdit ? "Edit Lab Operator" : "Add Lab Operator"}
        visible={isLabOperatorModalVisible}
        onCancel={handleCancelAddOrEditLabOperator}
        footer={[
          <Button key="Cancel" onClick={handleCancelAddOrEditLabOperator}>
            Cancel
          </Button>,
          <Button
            key="Add Operator"
            type="primary"
            onClick={addOrEditLabOperatorHandleSubmit(
              handleAddOrEditLabOperator
            )}
            loading={isLabOperatorAddingOrEditing}
          >
            {isLabOperatorEdit ? "Edit Operator" : "Add Operator"}
          </Button>,
        ]}
      >
        <AddOperator
          control={addOrEditLabOperatorControl}
          errors={addOrEditLabOperatorErrors}
          watch={addOrEditLabOperatorWatch}
          isEdit={isLabOperatorEdit}
        />
      </Modal>
    </div>
  );
};

export default LabListing;
